<template>
  <LegalDocView
    type="data-deletion"
    :title="$t('dataDeletion.title', 'Data deletion')"
    :empty-text="$t('dataDeletion.empty', 'The data deletion instructions have not been published yet.')"
  >
    <section>
      <h2 class="font-heading text-xl font-bold text-white mb-3">Deletion of your data</h2>
      <p>
        You have the right, at any time, to request the complete deletion of your account and
        the personal data associated with it (Art.&nbsp;17 GDPR). This page explains how to
        request deletion and which data it covers.
      </p>
    </section>

    <section>
      <h3 class="font-heading text-lg font-bold text-white mb-2">Sign-in via Facebook</h3>
      <p>
        If you signed in with Facebook, we store your Facebook user ID together with the name
        held at Facebook and, where applicable, your profile picture, so we can recognise your
        account. You can revoke our app's access yourself at any time:
      </p>
      <ol class="list-decimal pl-6 mt-3 space-y-1">
        <li>Open Facebook → <strong>Settings &amp; privacy</strong> → <strong>Settings</strong>.</li>
        <li>Select <strong>Apps and websites</strong>.</li>
        <li>Click this app in the list and choose <strong>Remove</strong>.</li>
      </ol>
      <p class="mt-3">
        That ends our access to your Facebook data. To also delete your account and everything
        we have stored, please submit the deletion request below.
      </p>
    </section>

    <section>
      <h3 class="font-heading text-lg font-bold text-white mb-2">Request full deletion</h3>
      <p>
        There is currently no self-service deletion inside the account, so please send us a
        short message — we will then delete your account and your data manually:
      </p>
      <ol class="list-decimal pl-6 mt-3 space-y-1">
        <li>
          Write to us via the
          <NuxtLink to="/contact" class="text-primary hover:underline">contact form</NuxtLink>
          or by email to the address given in our
          <NuxtLink to="/imprint" class="text-primary hover:underline">Imprint</NuxtLink>.
        </li>
        <li>Subject: <strong>“Data deletion”</strong>.</li>
        <li>
          Include the email address or username your account is registered with, so we can
          identify it unambiguously.
        </li>
      </ol>
      <p class="mt-3">
        We will confirm receipt and carry out the deletion without undue delay, and within
        <strong>30 days</strong> at the latest. You will receive a confirmation by email once
        it is done.
      </p>
    </section>

    <section>
      <h3 class="font-heading text-lg font-bold text-white mb-2">Which data is deleted</h3>
      <ul class="list-disc pl-6 space-y-1">
        <li>Your account and profile (name, email, profile picture),</li>
        <li>Your sign-in details, including the link to Facebook (Facebook ID),</li>
        <li>
          content you created and files you uploaded, unless a statutory retention obligation
          applies.
        </li>
      </ul>
      <p class="mt-3">
        <strong>Exception:</strong> data we are legally required to retain (for example
        invoicing and accounting records under §&nbsp;147 AO and §&nbsp;257 HGB — up to 10
        years) is only deleted once the respective retention period expires, and is blocked
        from further use until then.
      </p>
    </section>
  </LegalDocView>
</template>

<script setup>
import LegalDocView from '~/components/Legal/LegalDocView.vue'

useSeoMeta({
  title: 'Data deletion',
  description: 'How to delete your account and your personal data.'
})
</script>
